import React, { useContext, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { AuthContext } from '../context/authContext';

// -----------------------------------------------------------------------------
// Circuito de contactos de proyecto.
//
// Es la version grafica de docs/administracion-contactos.md (los diagramas
// mermaid), hecha con ECharts en vez de dibujar mermaid, para no sumar otra
// libreria: echarts-for-react ya esta en el proyecto y CicloDeVidaPage ya usa
// un series type 'graph' con layout 'none'.
//
// Lo que muestra es lo que suele confundir: que el catalogo
// (contactos_proyecto) es compartido y el cg_12 de cada obra es una copia
// congelada, y que "Agregar" escribe en el catalogo al toque mientras que
// "Guardar contactos" es el que da de baja.
// -----------------------------------------------------------------------------

const CATEGORIAS = [
    { name: 'Alta', itemStyle: { color: '#16a34a' } },
    { name: 'Baja', itemStyle: { color: '#dc2626' } },
    { name: 'Edición (pendiente)', itemStyle: { color: '#7c3aed' } },
    { name: 'Decisión', itemStyle: { color: '#d97706' } },
    { name: 'Resultado', itemStyle: { color: '#2563eb' } }
];

// categoria: indice en CATEGORIAS.
const nodo = (name, x, y, categoria) => ({
    name,
    x,
    y,
    category: categoria,
    symbol: 'roundRect',
    symbolSize: [190, 54],
    // width + overflow break: los nombres largos se envuelven solos y no se
    // salen del rectangulo.
    label: {
        show: true,
        position: 'inside',
        fontSize: 11,
        color: '#fff',
        lineHeight: 14,
        width: 172,
        overflow: 'break'
    }
});

const link = (source, target, texto) => ({
    source,
    target,
    ...(texto ? { label: { show: true, formatter: texto, fontSize: 11, color: '#374151' } } : {})
});

const CircuitoContactosPage = () => {
    const { role } = useContext(AuthContext);

    const option = useMemo(() => {
        const data = [
            // --- Alta (igual en los tres origenes: local) ---
            nodo('Agregar contacto', -700, 0, 0),
            nodo('Queda en la lista\nlocal (id = 0)\nno toca la base', -340, 0, 4),
            nodo('Lo crea el guardado\ndel formulario / Guardar', 120, 0, 0),
            nodo("Visible en\n'Buscar en catálogo'", 560, 0, 4),

            // --- Baja ---
            nodo('Quitás de la lista', -640, 380, 1),
            nodo('¿Cómo se persiste?', -320, 380, 3),
            nodo('Guardar la obra\n(Características generales)', 60, 260, 1),
            nodo('Se saca del cg_12\nde esa obra\n(catálogo intacto)', 440, 260, 1),
            nodo('Guardar la ONG\no Guardar contactos (PUT)', 60, 520, 1),
            nodo('¿Alguna obra lo usa?', 440, 520, 3),
            nodo('Bloqueado: vuelve\ncon el código de la obra', 800, 460, 4),
            nodo('Baja lógica\n(activo = false)', 800, 620, 4),

            // --- Edición ---
            nodo('Editar los datos', -640, 820, 2),
            nodo('Hoy no existe', -320, 820, 4),
            nodo('Workaround: Quitar + Agregar\n(crea OTRO, no lo edita)', -320, 980, 2),
            nodo('¿Alcance?\n(si se implementa)', 60, 820, 3),
            nodo('Edita el catálogo\n→ propagación diferida', 60, 980, 2),
            nodo('Propaga a las obras\n(replication_role=replica)', 440, 980, 2)
        ];

        const links = [
            link('Agregar contacto', 'Queda en la lista\nlocal (id = 0)\nno toca la base'),
            link('Queda en la lista\nlocal (id = 0)\nno toca la base', 'Lo crea el guardado\ndel formulario / Guardar'),
            link('Lo crea el guardado\ndel formulario / Guardar', "Visible en\n'Buscar en catálogo'"),

            link('Quitás de la lista', '¿Cómo se persiste?'),
            link('¿Cómo se persiste?', 'Guardar la obra\n(Características generales)', 'Características generales'),
            link('¿Cómo se persiste?', 'Guardar la ONG\no Guardar contactos (PUT)', 'Nueva ONG / Contactos de proyecto'),
            link('Guardar la obra\n(Características generales)', 'Se saca del cg_12\nde esa obra\n(catálogo intacto)'),
            link('Guardar la ONG\no Guardar contactos (PUT)', '¿Alguna obra lo usa?'),
            link('¿Alguna obra lo usa?', 'Bloqueado: vuelve\ncon el código de la obra', 'Sí'),
            link('¿Alguna obra lo usa?', 'Baja lógica\n(activo = false)', 'No'),

            link('Editar los datos', 'Hoy no existe'),
            link('Editar los datos', '¿Alcance?\n(si se implementa)'),
            link('Hoy no existe', 'Workaround: Quitar + Agregar\n(crea OTRO, no lo edita)'),
            link('¿Alcance?\n(si se implementa)', 'Edita el catálogo\n→ propagación diferida', 'catálogo'),
            link('¿Alcance?\n(si se implementa)', 'Propaga a las obras\n(replication_role=replica)', 'obras')
        ];

        return {
            tooltip: {
                formatter: (params) => {
                    if (params.dataType === 'edge') {
                        const l = params.data;
                        return `${l.source} → ${l.target}${l.label ? '<br/>' + l.label.formatter : ''}`;
                    }
                    return params.name.replace(/\n/g, '<br/>');
                }
            },
            legend: {
                data: CATEGORIAS.map(c => c.name),
                type: 'scroll',
                bottom: 0,
                icon: 'circle',
                itemWidth: 10,
                itemHeight: 10,
                textStyle: { fontSize: 11, color: '#374151' }
            },
            animationDuration: 500,
            series: [{
                type: 'graph',
                layout: 'none',
                roam: true,
                scaleLimit: { min: 0.3, max: 3 },
                data,
                links,
                categories: CATEGORIAS,
                edgeSymbol: ['none', 'arrow'],
                edgeSymbolSize: 8,
                label: { show: true },
                edgeLabel: { fontSize: 11 },
                lineStyle: { color: '#94a3b8', width: 1.4, curveness: 0 },
                itemStyle: { borderColor: '#ffffff', borderWidth: 1.5 },
                emphasis: { focus: 'adjacency', lineStyle: { width: 3, color: '#475569' } }
            }]
        };
    }, []);

    if (role !== 'admin') {
        return (
            <div className="p-6 text-center">
                <div className="p-6 max-w-md mx-auto mt-10 text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    Acceso denegado. Solo administradores.
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Circuito de contactos</h1>
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    solo admin
                </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-4xl">
                Cómo se administran los contactos de proyecto. El <strong>catálogo</strong> (
                <code>contactos_proyecto</code>) es compartido entre las obras del mismo scope; el{' '}
                <strong>cg_12</strong> de cada obra es una copia congelada de lo que tenía al guardarse.
                Arrastrá para mover el gráfico y usá la rueda para acercar.
            </p>

            {/* La diferencia que más confunde, arriba y bien visible: agregar y
                quitar son locales; el que persiste es el guardado. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 max-w-4xl">
                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3">
                    <h3 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-1">
                        "Agregar contacto" → <span className="uppercase">solo la lista</span>
                    </h3>
                    <p className="text-xs text-green-900 dark:text-green-200">
                        Suma el contacto a la lista <strong>sin tocar la base</strong>. Igual en la obra, en Nueva
                        ONG y en la página. No borra nada.
                    </p>
                </div>
                <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
                        Guardar → <span className="uppercase">crea y da de baja</span>
                    </h3>
                    <p className="text-xs text-red-900 dark:text-red-200">
                        Manda la lista entera (PUT): crea las altas y <strong>da de baja</strong> las que ya no
                        están, salvo las que usan obras (vuelven como "bloqueados" con el código de la obra).
                    </p>
                </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-4xl">
                El guardado que persiste depende del contexto: <strong>guardar la obra</strong> (Características
                generales), <strong>guardar la ONG</strong> (Nueva ONG), o <strong>"Guardar contactos"</strong> en
                la página. En la lista de contactos de la obra, "Quitar" solo saca del <code>cg_12</code> de esa
                obra al guardarla; el catálogo no se toca.
            </p>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                <ReactECharts option={option} style={{ height: '900px', width: '100%' }} notMerge={true} />
            </div>

            {/* Igual en los tres origenes para "Agregar"/"Quitar"; lo que cambia
                es qué guardado persiste la lista. */}
            <div className="mt-4 overflow-x-auto">
                <table className="text-sm border-collapse w-full max-w-6xl">
                    <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                            <th className="text-left px-3 py-2 border border-gray-200 dark:border-gray-700">Origen</th>
                            <th className="text-left px-3 py-2 border border-gray-200 dark:border-gray-700">Guardado que persiste</th>
                            <th className="text-left px-3 py-2 border border-gray-200 dark:border-gray-700">Qué hace</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-600 dark:text-gray-300">
                        <tr>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <strong>Características generales</strong>
                                <br /><span className="text-xs text-gray-500">obra nuevo / edit</span>
                            </td>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <strong>Guardar la obra</strong>
                            </td>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <code>resolverContactosParaObra</code>: crea/revive los contactos y arma el <code>cg_12</code> de esa obra. No toca el catálogo.
                            </td>
                        </tr>
                        <tr>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <strong>Nueva ONG</strong>
                                <br /><span className="text-xs text-gray-500">alta / edición de ONG</span>
                            </td>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <strong>Guardar la ONG</strong>
                            </td>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <code>sincronizarContactosONG</code>: crea/revive las de la lista y da de baja las que faltan (salvo las que usan obras).
                            </td>
                        </tr>
                        <tr>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <strong>Contactos de proyecto</strong>
                                <br /><span className="text-xs text-gray-500">página (+ modal por partido)</span>
                            </td>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <strong>"Guardar contactos"</strong>
                            </td>
                            <td className="px-3 py-2 border border-gray-200 dark:border-gray-700 align-top">
                                <code>sincronizarContactosScope</code>: igual que la ONG, y las que usan obras vuelven como "bloqueadas" con la obra. Además hay DELETE directo (409 si está en uso).
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 max-w-6xl">
                    "Agregar contacto" y "Quitar" son locales en los tres orígenes: solo cambian la lista de esa
                    pantalla. Nada toca la base hasta que se guarda.
                </p>
            </div>
        </div>
    );
};

export default CircuitoContactosPage;
